/**
 * Search GraphQL Service
 * Consumes search-related GraphQL queries from resources/core
 */

import { graphqlClient } from '@/services/core/graphqlClient';
import { SEARCH_QUERIES } from '@/services/resources';

export interface SearchPatientResult {
  pep_patient_id: number;
  patient_clinic_data_id: number;
  full_name: string;
  dob: string;
  home_phone_number?: string;
  mobile_phone_number?: string;
  email?: string;
  clinic_id: number;
  clinic_name: string;
  emr_id?: string;
}

export interface EDPatientDetailsResult {
  address_line_one?: string;
  condition?: string;
  address_line_two?: string;
  city?: string;
  clinic_facility_id?: number;
  clinic_facility_name?: string;
  clinic_id: number;
  clinic_name: string;
  created_by?: string;
  created_datetime?: string;
  dob: string;
  email?: string;
  emr_id?: string;
  emr_name?: string;
  emr_patient_clinic_data_id?: number;
  emr_patient_master_id?: number;
  enrollment_patient_id?: number;
  first_name: string;
  gender?: string;
  home_number?: string;
  identifier?: string;
  is_active?: boolean;
  last_name: string;
  middle_name?: string;
  mobile_number?: string;
  patient_group_id?: number;
  primary_insurance_payor?: string;
  primary_insurance_policy_number?: string;
  provider_id?: number;
  provider_name?: string;
  secondary_insurance_payor?: string;
  secondary_insurance_policy_number?: string;
  state?: string;
  updated_by?: string;
  updated_datetime?: string;
  work_phone?: string;
  zip_code?: string;
}

export class SearchGraphqlService {
  /**
   * Search patients by name (default search)
   */
  async searchPatientsDefault(hasuraToken: string, searchStr: string) {
    return await graphqlClient.query<{ pep_vw_consolidated_data: SearchPatientResult[] }>(
      SEARCH_QUERIES.SEARCH_PATIENTS_DEFAULT,
      { searchStr },
      hasuraToken
    );
  }

  /**
   * Search patients by multiple fields (name, phone, email, clinic, EMR ID)
   */
  async searchPatientsByField(hasuraToken: string, searchStr: string) {
    return await graphqlClient.query<{ pep_vw_consolidated_data: SearchPatientResult[] }>(
      SEARCH_QUERIES.SEARCH_PATIENTS_BY_FIELD,
      { searchStr },
      hasuraToken
    );
  }

  /**
   * Search patients by date of birth
   */
  async searchPatientsByDOB(hasuraToken: string, searchStr: string) {
    return await graphqlClient.query<{ pep_vw_consolidated_data: SearchPatientResult[] }>(
      SEARCH_QUERIES.SEARCH_PATIENTS_BY_DOB,
      { searchStr },
      hasuraToken
    );
  }

  /**
   * Search patients by specific field only
   */
  async searchPatientsBySpecificField(hasuraToken: string, searchStr: string, fieldName: string) {
    // Map field names to their corresponding queries
    const fieldQueryMap: { [key: string]: string } = {
      'full_name': SEARCH_QUERIES.SEARCH_BY_PATIENT_NAME,
      'mobile_phone_number': SEARCH_QUERIES.SEARCH_BY_MOBILE_PHONE,
      'home_phone_number': SEARCH_QUERIES.SEARCH_BY_HOME_PHONE,
      'email': SEARCH_QUERIES.SEARCH_BY_EMAIL,
      'clinic_name': SEARCH_QUERIES.SEARCH_BY_CLINIC_NAME,
      'emr_id': SEARCH_QUERIES.SEARCH_BY_EMR_ID
    };

    const query = fieldQueryMap[fieldName];
    if (!query) {
      // Fallback to the general field search if field is not mapped
      return await this.searchPatientsByField(hasuraToken, searchStr);
    }

    return await graphqlClient.query<{ pep_vw_consolidated_data: SearchPatientResult[] }>(
      query,
      { searchStr },
      hasuraToken
    );
  }

  /**
   * Get all patients (for default data loading)
   */
  async getAllPatients(hasuraToken: string) {
    return await graphqlClient.query<{ pep_vw_consolidated_data: SearchPatientResult[] }>(
      SEARCH_QUERIES.GET_ALL_PATIENTS,
      null,
      hasuraToken
    );
  }

  /**
   * Search patients by clinic
   */
  async searchPatientsByClinic(hasuraToken: string, clinicId: number, searchStr: string) {
    return await graphqlClient.query<{ pep_vw_consolidated_data: SearchPatientResult[] }>(
      SEARCH_QUERIES.SEARCH_PATIENTS_BY_CLINIC,
      { clinicId, searchStr },
      hasuraToken
    );
  }

  /**
   * Search patients for VPE Dashboard
   */
  async searchPatientsVPEDashboard(hasuraToken: string, clinicID: number, searchStr: string) {
    return await graphqlClient.query<{ pep_vw_consolidated_data: SearchPatientResult[] }>(
      SEARCH_QUERIES.SEARCH_PATIENTS_VPE_DASHBOARD,
      { clinicID, searchStr },
      hasuraToken
    );
  }

  /**
   * Intelligent two-step name search: exact match first, then word-by-word OR search
   */
  async searchPatientsIntelligentName(hasuraToken: string, exactMatch: string, wordSearch: string) {
    return await graphqlClient.query<{
      exact_match: SearchPatientResult[];
      word_match: SearchPatientResult[]
    }>(
      SEARCH_QUERIES.SEARCH_PATIENTS_INTELLIGENT_NAME,
      { exactMatch, wordSearch },
      hasuraToken
    );
  }

  /**
   * Get patient details for ED users with pagination
   */
  async getEDPatientDetails(hasuraToken: string, offset: number = 0, limit: number = 20) {
    return await graphqlClient.query<{ 
      pep_vw_patient_details: EDPatientDetailsResult[];
      pep_vw_patient_details_aggregate: {
        aggregate: {
          count: number;
        };
      };
    }>(
      SEARCH_QUERIES.GET_ED_PATIENT_DETAILS,
      { offset, limit },
      hasuraToken
    );
  }

  /**
   * Search ED patient details by name
   */
  async searchEDPatientByName(hasuraToken: string, searchStr: string, offset: number = 0, limit: number = 100) {
    return await graphqlClient.query<{ 
      pep_vw_patient_details: EDPatientDetailsResult[];
      pep_vw_patient_details_aggregate: {
        aggregate: {
          count: number;
        };
      };
    }>(
      SEARCH_QUERIES.SEARCH_ED_PATIENT_BY_NAME,
      { searchStr, offset, limit },
      hasuraToken
    );
  }

  /**
   * Search ED patient details by first AND last name (for multi-word searches)
   */
  async searchEDPatientByFirstAndLastName(hasuraToken: string, firstName: string, lastName: string, offset: number = 0, limit: number = 100) {
    return await graphqlClient.query<{ 
      pep_vw_patient_details: EDPatientDetailsResult[];
      pep_vw_patient_details_aggregate: {
        aggregate: {
          count: number;
        };
      };
    }>(
      SEARCH_QUERIES.SEARCH_ED_PATIENT_BY_FIRST_AND_LAST_NAME,
      { firstName, lastName, offset, limit },
      hasuraToken
    );
  }

  /**
   * Search ED patient details by DOB
   */
  async searchEDPatientByDOB(hasuraToken: string, searchStr: string, offset: number = 0, limit: number = 100) {
    return await graphqlClient.query<{ 
      pep_vw_patient_details: EDPatientDetailsResult[];
      pep_vw_patient_details_aggregate: {
        aggregate: {
          count: number;
        };
      };
    }>(
      SEARCH_QUERIES.SEARCH_ED_PATIENT_BY_DOB,
      { searchStr, offset, limit },
      hasuraToken
    );
  }

  /**
   * Search ED patient details by phone
   */
  async searchEDPatientByPhone(hasuraToken: string, searchStr: string, offset: number = 0, limit: number = 100) {
    return await graphqlClient.query<{ 
      pep_vw_patient_details: EDPatientDetailsResult[];
      pep_vw_patient_details_aggregate: {
        aggregate: {
          count: number;
        };
      };
    }>(
      SEARCH_QUERIES.SEARCH_ED_PATIENT_BY_PHONE,
      { searchStr, offset, limit },
      hasuraToken
    );
  }

  /**
   * Search ED patient details by email
   */
  async searchEDPatientByEmail(hasuraToken: string, searchStr: string, offset: number = 0, limit: number = 100) {
    return await graphqlClient.query<{ 
      pep_vw_patient_details: EDPatientDetailsResult[];
      pep_vw_patient_details_aggregate: {
        aggregate: {
          count: number;
        };
      };
    }>(
      SEARCH_QUERIES.SEARCH_ED_PATIENT_BY_EMAIL,
      { searchStr, offset, limit },
      hasuraToken
    );
  }

  /**
   * Search ED patient details by clinic
   */
  async searchEDPatientByClinic(hasuraToken: string, searchStr: string, offset: number = 0, limit: number = 100) {
    return await graphqlClient.query<{ 
      pep_vw_patient_details: EDPatientDetailsResult[];
      pep_vw_patient_details_aggregate: {
        aggregate: {
          count: number;
        };
      };
    }>(
      SEARCH_QUERIES.SEARCH_ED_PATIENT_BY_CLINIC,
      { searchStr, offset, limit },
      hasuraToken
    );
  }

  /**
   * Search ED patient details by EMR ID
   */
  async searchEDPatientByEMR(hasuraToken: string, searchStr: string, offset: number = 0, limit: number = 100) {
    return await graphqlClient.query<{ 
      pep_vw_patient_details: EDPatientDetailsResult[];
      pep_vw_patient_details_aggregate: {
        aggregate: {
          count: number;
        };
      };
    }>(
      SEARCH_QUERIES.SEARCH_ED_PATIENT_BY_EMR,
      { searchStr, offset, limit },
      hasuraToken
    );
  }
}

// Export singleton instance
export const searchGraphqlService = new SearchGraphqlService();
export default searchGraphqlService;